import { useEffect, type RefObject } from "react";

import type { Map, MapBrowserEvent } from "ol";
import type VectorSource from "ol/source/Vector";
import { displayForecastDataWithStation } from "../helpers";
import { IForecastWithStation } from "@/types/daily-forecast-type";
import { stationMapSchema } from "@/schemas/map-schema";
import { WeatherDispatch } from "@/store/type";
import { useDispatch } from "@wordpress/data";
import { WEATHER_STORE } from "@/store/store";

export const useWeatherForecastMap = ({
	vectorSourceRef,
	mapRefOl,
	isObsForecastPage,
	forecastDataWithStations,
	isArabic,
}: {
	vectorSourceRef: RefObject<VectorSource | null>;
	mapRefOl: RefObject<Map | null>;
	isObsForecastPage: boolean;
	forecastDataWithStations?: {
		data: IForecastWithStation[];
		show: boolean;
		selectedDate: Date;
	};
	isArabic: boolean;
}) => {
	const { searchCityById, setDailyForecastStationId } = useDispatch(
		WEATHER_STORE,
	) as WeatherDispatch;

	useEffect(() => {
		if (!vectorSourceRef.current || !forecastDataWithStations?.show) {
			return;
		}

		const markerFeatures = displayForecastDataWithStation(
			forecastDataWithStations.data,
			forecastDataWithStations.selectedDate,
			isArabic,
		);

		vectorSourceRef.current.clear();
		vectorSourceRef.current.addFeatures([...markerFeatures]);
	}, [forecastDataWithStations]);

	/*==========================
      Click sur les station dans observations/forecast
    ==========================*/

	useEffect(() => {
		if (!vectorSourceRef.current || !mapRefOl.current || isObsForecastPage)
			return;
		const map = mapRefOl.current;

		const handleClick = (
			e: MapBrowserEvent<PointerEvent | KeyboardEvent | WheelEvent>,
		) => {
			map.forEachFeatureAtPixel(e.pixel, (feature) => {
				const data = feature.get("data");
				const { data: markerData, error } = stationMapSchema.safeParse(data);

				if (error) return false;

				searchCityById({
					cityId: markerData.station_id,
					cityNameAr: markerData.name_ar,
					cityNameEn: markerData.name_en,
				});

				setDailyForecastStationId(markerData.station_id);

				window.location.href = `https://dev.vigiclim.exelys.io/en/weather-forecast/?${markerData.station_id}`;

				return true;
			});
		};

		map.on("singleclick", handleClick);

		return () => {
			map.un("singleclick", handleClick);
		};
	}, [isObsForecastPage, vectorSourceRef, mapRefOl]);
};
